import { clsx, type ClassValue } from "clsx";
import moment from "moment";
import { twMerge } from "tailwind-merge";
import { z } from "zod";
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Recursive function to extract required fields
export const getRequiredFields = (schema: any, prefix = ""): string[] => {
  return Object.entries(schema.shape)?.flatMap(([key, field]) => {
    const fieldName = prefix ? `${prefix}.${key}` : key;

    if (field instanceof z.ZodObject) {
      return getRequiredFields(field, fieldName); // Recurse for nested objects
    } else if (field instanceof z.ZodArray) {
      return getRequiredFields(field._def.type, fieldName); // Check array elements
    } else if (field instanceof z.ZodString && !field.isOptional()) {
      return fieldName; // Add required field
    } else if (field instanceof z.ZodNumber && !field.isOptional()) {
      return fieldName; // Add required field
    } else if (field instanceof z.ZodDate && !field.isOptional()) {
      return fieldName; // Add required field
    }

    return [];
  });
};

export const removeEmptyFields = (data: any) => {
  return Object.fromEntries(
    Object.entries(data).filter(([_, value]) => value !== undefined)
  );
};

export const setEmptyFieldsToNull = (data: any) => {
  Object.keys(data).forEach((key) => {
    if (data[key] == "") {
      data[key] = null;
    }
  });
  return data;
};

export const sortByDate = (data: any, key: string) => {
  const a = new Date(data[key]).getTime();
  const b = new Date(data[key]).getTime();
  return a < b ? -1 : a > b ? 1 : 0;
};

export const formatAmount = (
  amount: number | string,
  maximumFractionDigits: number = 0,
  locale: string = "en-US",
  currency: string = "USD"
): string => {
  const numericAmount =
    typeof amount === "string" ? parseFloat(amount) : amount;

  if (isNaN(numericAmount)) return "$0";

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    maximumFractionDigits: maximumFractionDigits,
  }).format(numericAmount);
};

export const yearOptions = (length: number) => {
  return new Array(length)
    .fill(0)
    .map((_, index: number) => ({
      label: (moment().year() - 2 + index).toString(),
      value: (moment().year() - 2 + index).toString(),
    }))
    .reverse();
};

export const monthOptions = [
  { label: "All", value: "all" },
  ...moment.months().map((month, index) => ({
    label: month,
    value: (index + 1).toString().padStart(2, "0"),
  })),
];

export const ROLE_ADMIN = "ROLE_ADMIN";
export const ROLE_OWNER = "ROLE_OWNER";
export const ROLE_BOM = "ROLE_BOM";

export const roles = {
  ROLE_ADMIN: "Admin",
  ROLE_OWNER: "Owner",
  ROLE_BOM: "BOM",
};

export const capitalize = (str: string): string => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const formatTwoDigits = (num: number): string => {
  return num < 10 ? `0${num}` : `${num}`;
};

export const formatCurrencyShort = (value: number): string => {
  if (value >= 1_000_000_000) {
    return `$${(value / 1_000_000_000).toFixed(1)}B`;
  } else if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M`;
  } else if (value >= 1_000) {
    return `$${(value / 1_000).toFixed(0)}K`;
  }
  return `$${value}`;
};

export const calculatePercentageChange = (
  current: number,
  previous: number
): { value: string; progress: string } => {
  if (previous == 0) return { value: "0", progress: "" };
  const change = ((current - previous) / previous) * 100;
  const sign = change >= 0 ? "+" : "";

  return {
    value: change ? `${sign}${(change || 0).toFixed(2)}%` : "0",
    progress: change ? (change >= 0 ? "up" : "down") : "",
  };
};

export const invoiceParkStatus: any = {
  all: "",
  draft: {
    label: "Parked",
    color: "border-primary bg-blue-100 text-primary",
    status: "DRAFT",
  },
  pending_for_approval: {
    label: "Pending For Approval",
    status: "PENDING_APPROVAL",
    color: "border-yellow-600 bg-yellow-100 text-yellow-600",
  },
  pending_for_pm_approval: {
    label: "Pending For PM Approval",
    color: "border-yellow-600 bg-yellow-100 text-yellow-600",
    status: "PENDING_APPROVAL",
    approvalStatus: "PM:PENDING_APPROVAL",
  },
  pending_for_my_approval: {
    label: "Pending For My Approval",
    color: "border-yellow-600 bg-yellow-100 text-yellow-600",
    status: "PENDING_APPROVAL",
    approvalStatus: "BOM:PENDING_APPROVAL",
  },
  pending_for_board_approval: {
    label: "Pending For Board Approval",
    color: "border-yellow-600 bg-yellow-100 text-yellow-600",
    status: "PENDING_APPROVAL",
    approvalStatus: "BOARD:PENDING_APPROVAL",
  },
  pending_for_agm_approval: {
    label: "Pending For AGM Approval",
    color: "border-yellow-600 bg-yellow-100 text-yellow-600",
    status: "PENDING_APPROVAL",
    approvalStatus: "AGM:PENDING_APPROVAL",
  },
  pending_approval: {
    label: "Pending Approval",
    color: "border-yellow-600 bg-yellow-100 text-yellow-600",
    status: "PENDING_APPROVAL",
  },
  approved: {
    label: "Approved",
    color: "border-green-600 bg-green-100 text-green-600",
    status: "APPROVED",
  },
  rejected: {
    label: "Rejected",
    color: "border-red-600 bg-red-100 text-red-600",
    status: "REJECTED",
  },
  posted: {
    label: "Posted",
    color: "border-green-600 bg-green-100 text-green-600",
    status: "POSTED",
  },
};

export const invoiceActionStatus = {
  all: "all",
  draft: "draft",
  pending_for_approval: "pending_for_approval",
  pending_for_pm_approval: "pending_for_pm_approval",
  pending_for_my_approval: "pending_for_my_approval",
  pending_for_board_approval: "pending_for_board_approval",
  pending_for_agm_approval: "pending_for_agm_approval",
  approved: "approved",
  rejected: "rejected",
  posted: "posted",
  reversed: "reversed",
};

export const paymentStatus: any = {
  all: "",
  posted: {
    label: "Posted Invoice Amount",
    status: "POSTED",
    color: "border-primary bg-blue-100 text-primary",
  },
  pending: {
    label: "Pending Invoice",
    status: "PENDING",
    color: "border-yellow-600 bg-yellow-100 text-yellow-600",
  },
  paid: {
    label: "Paid Invoice",
    status: "PAID",
    color: "border-green-600 bg-green-100 text-green-600",
  },
};

export const billingStatus: any = {
  all: "",
  billing_config: {
    label: "Billing Configuration",
    action: "billing_config",
  },
  pending_for_posting: {
    label: "Pending For Posting",
    action: "pending_for_posting",
  },
  billed_for_period: {
    label: "Billed For the Period",
    action: "billed_for_period",
  },
  total_collection: {
    label: "Total Collection",
    action: "total_collection",
  },
  billing_log: {
    label: "Billing Log",
    action: "billing_log",
  },
};

export const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const OVERVIEW_IMAGES = [
  {
    src: "/img/banner/overview_img_bg.png",
    alt: "Background",
    className: `
      w-full
      max-w-[350px] sm:max-w-[400px] md:max-w-[600px] 
      h-auto object-contain
      mx-auto
    `,
    style: {},
  },
  {
    src: "/img/banner/overview_img_1.png",
    alt: "Overlay 1",
    className: `
      absolute 
      top-10 
      right-10 sm:right-10 md:right-[50px]
      max-w-[180px] sm:max-w-[250px] md:max-w-[300px] 
      h-auto object-contain pointer-events-none
    `,
    style: { transform: "none" },
  },
  {
    src: "/img/banner/overview_img_2.png",
    alt: "Overlay 2",
    className: `
      absolute 
      top-1/2 
      -right-2 sm:right-6 md:-right-15
      -translate-y-1/2 
      max-w-[80px] sm:max-w-[110px] md:max-w-[150px]
      h-auto object-contain pointer-events-none float-animation
    `,
    style: {},
  },
];

export const LEADERSHIP_IMAGES = [
  {
    src: "/img/banner/overview_img_bg.png",
    alt: "Background",
    className: `
      w-full
      max-w-[320px] sm:max-w-[400px] md:max-w-[600px] 
      h-auto object-contain
      mx-auto
    `,
    style: {},
  },
  {
    src: "/img/banner/ourleadership_img_1.png",
    alt: "Overlay 1",
    className: `
      absolute 
      top-10
      right-10 sm:right-10 md:right-[50px]
      max-w-[180px] sm:max-w-[250px] md:max-w-[300px] 
      h-auto object-contain pointer-events-none
    `,
    style: { transform: "none" },
  },
  {
    src: "/img/banner/ourleadership_img_2.png",
    alt: "Overlay 2",
    className: `
      absolute 
      top-1/2 
      -right-2 sm:right-6 md:-right-15
      -translate-y-1/2 
      max-w-[80px] sm:max-w-[110px] md:max-w-[150px]
      h-auto object-contain pointer-events-none float-animation
    `,
    style: {},
  },
];

export const HISTORY_IMAGES = [
  {
    src: "/img/banner/overview_img_bg.png",
    alt: "Background",
    className: `
      w-full
      max-w-[320px] sm:max-w-[400px] md:max-w-[600px] 
      h-auto object-contain
      mx-auto
    `,
    style: {},
  },
  {
    src: "/img/banner/ourhistory_img_1.png",
    alt: "Overlay 1",
    className: `
      absolute 
      top-10
      right-10 sm:right-10 md:right-[50px]
      max-w-[180px] sm:max-w-[250px] md:max-w-[300px] 
      h-auto object-contain pointer-events-none
    `,
    style: { transform: "none" },
  },
  {
    src: "/img/banner/ourhistory_img_2.png",
    alt: "Overlay 2",
    className: `
      absolute 
      top-1/2 
      -right-2 sm:right-6 md:-right-15
      -translate-y-1/2 
      max-w-[80px] sm:max-w-[110px] md:max-w-[150px]
      h-auto object-contain pointer-events-none float-animation
    `,
    style: {},
  },
];

export const PRODUCTS_IMAGES = [
  {
    src: "/img/banner/overview_img_bg.png",
    alt: "Background",
    className: `
      w-full
      max-w-[320px] sm:max-w-[400px] md:max-w-[600px] 
      h-auto object-contain
      mx-auto
    `,
    style: {},
  },
  {
    src: "/img/banner/product_img_1.png",
    alt: "Overlay 1",
    className: `
      absolute 
      top-10 
      right-10 sm:right-10 md:right-[50px]
      max-w-[180px] sm:max-w-[250px] md:max-w-[300px] 
      h-auto object-contain pointer-events-none
    `,
    style: { transform: "none" },
  },
  {
    src: "/img/banner/product_img_2.png",
    alt: "Overlay 2",
    className: `
      absolute 
      top-1/2 
      -right-2 sm:right-6 md:-right-15
      -translate-y-1/2 
      max-w-[80px] sm:max-w-[110px] md:max-w-[150px]
      h-auto object-contain pointer-events-none float-animation
    `,
    style: {},
  },
];

export const RESEARCH_IMAGES = [
  {
    src: "/img/banner/overview_img_bg.png",
    alt: "Background",
    className: `
      w-full
      max-w-[320px] sm:max-w-[400px] md:max-w-[630px] 
      h-auto object-contain
      mx-auto
    `,
    style: {},
  },
  {
    src: "/img/banner/researchinnovation_img_1.png",
    alt: "Overlay 1",
    className: `
      absolute 
      top-10
      right-10 sm:right-10 md:right-[50px]
      max-w-[180px] sm:max-w-[250px] md:max-w-[350px] 
      h-auto object-contain pointer-events-none
    `,
    style: { transform: "none" },
  },
  {
    src: "/img/banner/researchinnovation_img_2.png",
    alt: "Overlay 2",
    className: `
      absolute 
      top-1/2 
      -right-3 sm:right-6 md:right-[-50px]
      -translate-y-1/2 
      max-w-[80px] sm:max-w-[110px] md:max-w-[150px]
      h-auto object-contain pointer-events-none float-animation
    `,
    style: {},
  },
];

  export const CAREER_IMAGES = [
  {
    src: "/img/banner/overview_img_bg.png",
    alt: "Background",
    className: `
      w-full
      max-w-[320px] sm:max-w-[400px] md:max-w-[600px] 
      h-auto object-contain
      mx-auto
    `,
    style: {},
  },
  {
    src: "/img/banner/career_img_1.png",
    alt: "Overlay 1",
    className: `
      absolute 
      top-10
      right-10 sm:right-10 md:right-[50px]
      max-w-[180px] sm:max-w-[250px] md:max-w-[300px] 
      h-auto object-contain pointer-events-none
    `,
    style: { transform: "none" },
  },
  {
    src: "/img/banner/career_img_2.png",
    alt: "Overlay 2",
    className: `
      absolute 
      top-1/2 
      -right-2 sm:right-6 md:-right-15
      -translate-y-1/2 
      max-w-[80px] sm:max-w-[110px] md:max-w-[150px]
      h-auto object-contain pointer-events-none float-animation
    `,
    style: {},
  },
];

export const CONTACT_IMAGES = [
  {
    src: "/img/banner/overview_img_bg.png",
    alt: "Background",
    className: `
      w-full
      max-w-[320px] sm:max-w-[400px] md:max-w-[600px] 
      h-auto object-contain
      mx-auto
    `,
    style: {},
  },
  {
    src: "/img/banner/contact_img_1.png",
    alt: "Overlay 1",
    className: `
      absolute 
      top-10 
      right-10 sm:right-10 md:right-[50px]
      max-w-[180px] sm:max-w-[250px] md:max-w-[300px] 
      h-auto object-contain pointer-events-none
    `,
    style: { transform: "none" },
  },
  {
    src: "/img/banner/contact_img_2.png",
    alt: "Overlay 2",
    className: `
      absolute 
      top-1/2 
      -right-2 sm:right-6 md:-right-15
      -translate-y-1/2 
      max-w-[80px] sm:max-w-[110px] md:max-w-[150px]
      h-auto object-contain pointer-events-none float-animation
    `,
    style: {},
  },
];


export const UPDATE_IMAGES = [
  {
    src: "/img/banner/overview_img_bg.png",
    alt: "Background",
    className: `
      w-full
      max-w-[320px] sm:max-w-[400px] md:max-w-[600px] 
      h-auto object-contain
      mx-auto
    `,
    style: {},
  },
  {
    src: "/img/banner/update_img_1.png",
    alt: "Overlay 1",
    className: `
      absolute 
      top-10 
      right-10 sm:right-10 md:right-[50px]
      max-w-[180px] sm:max-w-[250px] md:max-w-[300px] 
      h-auto object-contain pointer-events-none
    `,
    style: { transform: "none" },
  },
  {
    src: "/img/banner/update_img_2.png",
    alt: "Overlay 2",
    className: `
      absolute 
      top-1/2 
      -right-2 sm:right-6 md:-right-15
      -translate-y-1/2 
      max-w-[80px] sm:max-w-[110px] md:max-w-[150px]
      h-auto object-contain pointer-events-none float-animation
    `,
    style: {},
  },
];

export const BLOGS_IMAGES = [
  {
    src: "/img/banner/overview_img_bg.png",
    alt: "Background",
    className: `
      w-full
      max-w-[320px] sm:max-w-[400px] md:max-w-[600px] 
      h-auto object-contain
      mx-auto
    `,
    style: {},
  },
  {
    src: "/img/banner/blog_img_1.png",
    alt: "Overlay 1",
    className: `
      absolute 
      top-10 
      right-10 sm:right-10 md:right-[50px]
      max-w-[180px] sm:max-w-[250px] md:max-w-[300px] 
      h-auto object-contain pointer-events-none
    `,
    style: { transform: "none" },
  },
  {
    src: "/img/banner/blog_img_2.png",
    alt: "Overlay 2",
    className: `
      absolute 
      top-1/2 
      -right-2 sm:right-6 md:-right-15
      -translate-y-1/2 
      max-w-[80px] sm:max-w-[110px] md:max-w-[150px]
      h-auto object-contain pointer-events-none float-animation
    `,
    style: {},
  },
];